/**
 * Email Templates editor (admin).
 *
 * Edits the subject/body wording of helpdesk + notification emails stored in
 * biz_email_templates (the {{variable}} placeholders are interpolated at send
 * time). The branded shell — logo, colors, header/footer — is per-app branding
 * on the widget key (see Widget Management), not here.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Mail, Save } from 'lucide-react';

interface EmailTemplate {
  templateKey: string;
  name: string;
  subjectTemplate: string;
  bodyTemplate: string;
  isActive: boolean;
}

export function EmailTemplatesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<EmailTemplate | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => api.getEmailTemplates(),
  });
  const templates = (data?.data || []) as EmailTemplate[];

  // Default selection to the first template once loaded.
  useEffect(() => {
    if (!selectedKey && templates.length > 0) setSelectedKey(templates[0].templateKey);
  }, [templates, selectedKey]);

  // Load the selected template into the editable draft.
  const selected = templates.find((t) => t.templateKey === selectedKey) || null;
  useEffect(() => {
    if (selected) setDraft({ ...selected });
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMutation = useMutation({
    mutationFn: (t: EmailTemplate) =>
      api.upsertEmailTemplate(t.templateKey, {
        name: t.name,
        subjectTemplate: t.subjectTemplate,
        bodyTemplate: t.bodyTemplate,
        isActive: t.isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      toast({ title: 'Template saved' });
    },
    onError: () => toast({ title: 'Failed to save template', variant: 'destructive' }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Email Templates</h1>
        <p className="text-muted-foreground">
          Edit the wording of automated emails. Logo, color, and header/footer branding are set per app in Widget Management.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Template list */}
          <div className="space-y-1">
            {templates.map((t) => (
              <button
                key={t.templateKey}
                onClick={() => setSelectedKey(t.templateKey)}
                className={`w-full text-left rounded-md px-3 py-2 text-sm transition-colors ${
                  selectedKey === t.templateKey ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{t.name || t.templateKey}</span>
                  {!t.isActive && <span className="ml-auto text-[10px] opacity-70">off</span>}
                </div>
                <div className={`text-[11px] mt-0.5 truncate ${selectedKey === t.templateKey ? 'opacity-80' : 'text-muted-foreground'}`}>
                  {t.templateKey}
                </div>
              </button>
            ))}
          </div>

          {/* Editor */}
          <div className="md:col-span-2">
            {draft ? (
              <Card>
                <CardContent className="p-5 space-y-4">
                  <div>
                    <Label className="text-xs">Template name</Label>
                    <Input className="mt-1" value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Subject</Label>
                    <Input className="mt-1" value={draft.subjectTemplate}
                      onChange={(e) => setDraft({ ...draft, subjectTemplate: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Body (HTML; supports {'{{variables}}'})</Label>
                    <Textarea className="mt-1 font-mono text-xs" rows={14} value={draft.bodyTemplate}
                      onChange={(e) => setDraft({ ...draft, bodyTemplate: e.target.value })} />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Common variables: {'{{ticketNumber}}'}, {'{{subject}}'}, {'{{clientName}}'}, {'{{priority}}'}, {'{{expectedResponseTime}}'}, {'{{newStatus}}'}, {'{{agentName}}'}, {'{{messageContent}}'}, {'{{surveyUrl}}'}. The logo/color header and footer are added automatically.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="tmpl-active" checked={draft.isActive}
                      onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                      className="rounded border-gray-300" />
                    <Label htmlFor="tmpl-active" className="mb-0 text-sm">Active</Label>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => saveMutation.mutate(draft)} disabled={saveMutation.isPending}>
                      <Save className="h-4 w-4 mr-2" />
                      {saveMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                    {selected && (
                      <Button variant="outline" onClick={() => setDraft({ ...selected })} disabled={saveMutation.isPending}>
                        Reset
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <p className="text-sm text-muted-foreground">Select a template to edit.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
